import { Guid } from '../../../../store/model/guid';
import { isCompleted } from '../../models/ItemsCompletion';
import { UserProgressService } from '../UserProgressService';
import { findHeaviestPathInTree } from './findHeaviestPathInTree';
import { findSection } from './findSection';
import { ensureSection, isSection, isSequencedFlowItem, Item, Section, SequencedFlowItem } from '@ael/core-api';
import { List } from 'immutable';

export class Segment {
  content: Guid[];
  size: number;
  sections: Section[];
  constructor(items: Item[]) {
    this.content = items.filter((item) => !isSection(item)).map((item) => item.id);
    this.sections = items
      .filter((item) => isSection(item))
      .map((item) => {
        ensureSection(item);
        return item;
      });
    this.size = this.content.length;
  }

  hasBranches() {
    return this.sections.length > 0;
  }

  findBranch(branchId: Guid) {
    return this.sections.find((section: Section) => section.id === branchId);
  }
}

export class SequencedFlowContentEvaluator {
  private readonly _statusEvaluator: UserProgressService;
  private readonly _getItem: (id: Guid) => Item;
  private readonly _evaluatedSegment: SequencedFlowItem | Section;
  // Required to calculate overall progress, as we need to find preceding segments
  private readonly _sequencedFlowItem?: SequencedFlowItem;

  constructor(
    item: SequencedFlowItem | Section,
    statusEvaluator: UserProgressService,
    getItem: (id: Guid) => Item,
    sequencedFlowItem?: SequencedFlowItem
  ) {
    this._sequencedFlowItem = sequencedFlowItem;
    this._getItem = getItem;
    this._statusEvaluator = statusEvaluator;
    this._evaluatedSegment = item;
  }

  run() {
    const longestPathToComplete = this.getLongestPathToEnd(this._evaluatedSegment);
    const previousSections = this.calculatePrecedingContentCount();
    const completed = this.calculateCompletedContentItems(longestPathToComplete.itemIds);

    return {
      itemsCount: previousSections + longestPathToComplete.itemIds.length,
      completedItemsCount: previousSections + completed,
    };
  }

  private getLongestPathToEnd(item: Section | SequencedFlowItem): {
    itemIds: Guid[];
  } {
    const longestPath = findHeaviestPathInTree(
      this.convertSequencedSectionIntoNode(item),
      (node) => !node.hasBranches(),
      (node) => node.sections.map(this.convertSequencedSectionIntoNode),
      (node) => node.size
    );
    const itemsInLongestNode = longestPath.branches.reduce((items: string[], node: Segment): string[] => {
      return [...node.content, ...items];
    }, []);

    return { itemIds: itemsInLongestNode };
  }

  private calculatePrecedingContentCount() {
    if (!this._sequencedFlowItem) {
      return 0;
    }
    const pathToCurrent = findSection(this._evaluatedSegment, this._sequencedFlowItem, this._getSubnodes);
    if (!pathToCurrent) {
      return 0;
    }

    return (
      this.getSectionContentCount(pathToCurrent) + this.convertSequencedSectionIntoNode(this._sequencedFlowItem).size
    );
  }

  private getSectionContentCount = (sectionsToGoThrough: List<Guid>) => {
    return sectionsToGoThrough.reduce((total: number, sectionId: Guid) => {
      const section = this._getItem(sectionId);
      ensureSection(section);
      const segment = this.convertSequencedSectionIntoNode(section);
      return total + segment.size;
    }, 0);
  };

  private calculateCompletedContentItems = (itemIds: Guid[]) => {
    const items = itemIds.map((itemId: Guid) => this._getItem(itemId));
    const content = items.filter((item: Item) => !isSection(item));
    const completed = content.filter((item: Item) => {
      const result = this._statusEvaluator.evaluate(item);

      return isCompleted(result);
    });

    return completed.length;
  };

  private convertSequencedSectionIntoNode = (item: SequencedFlowItem | Section): Segment => {
    const itemsInSegment = isSequencedFlowItem(item) ? item.contentItems : item.children;
    const node = new Segment(itemsInSegment.map(this._getItem).toArray());
    return node;
  };

  private _getSubnodes = (item: SequencedFlowItem | Section): Section[] | null => {
    const node = this.convertSequencedSectionIntoNode(item);

    return node.hasBranches() ? node.sections : null;
  };
}
